import { ImeModeKinds } from "..";
import { AbstractPlainTextItem } from "./AbstractPlainTextItem";
import { TypeKeys } from "./TypeKeys";
import { IImeControllable } from "./IImeControllable";
import { IEmptyValidatable } from "./IEmptyValidatable";
import { INameGroupEmptyValidatable } from "./INameGroupEmptyValidatable";
import { IPaintableOnEditing } from "./IPaintableOnEditing";
import { PaintStyle } from "../../drawing";
import { UpdateKinds } from "../UpdateKinds";
import { ExternalSubItem } from "./ExternalSubItem";
import { IExternalDataApplicable } from "./IExternalDataApplicable";
import { IPasteRequestable } from "./IPasteRequestable";
import { IPasteApplicable } from "./IPasteApplicable";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { IFontSizeAutoAdjustable } from "./IFontSizeAutoAdjustable";
import { AutoNotePageKinds } from "../AutoNotePageKinds";
import { DataValidationKinds } from "../DataValidationKinds";
import { ITextValidatable } from "./ITextValidatable";
import { ISubNameSpecifiable } from "./ISubNameSpecifiable";
import { INaturalTextSpecifiable } from "./INaturalTextSpecifiable";

// V1フォーマット上ではscriptタグ
// [参考資料]
// \\192.168.2.222\共通フォルダ他\★Documaker\01_DocuMaker導入資料\機能仕様書\機能別\外部データ一覧選択.docx
export class ExternalDataItem extends AbstractPlainTextItem implements
  IPaintableOnEditing,
  IImeControllable,
  IEmptyValidatable,
  INameGroupEmptyValidatable,
  IExternalDataApplicable,
  IPasteRequestable,
  IFontSizeAutoAdjustable,
  ITextValidatable,
  ISubNameSpecifiable,
  INaturalTextSpecifiable {
  constructor(value: any) {
    super(TypeKeys.ExternalDataItem, value);
  }

  public paintStyleOnEditing: PaintStyle = new PaintStyle();
  public imeMode: ImeModeKinds = ImeModeKinds.AllOn;
  public enableSingleEmptyCheck: boolean = false;
  public enableEmptyCheckConfirmation: boolean = false;
  public alertGroupKey: string = null;
  public dataValidationKind: DataValidationKinds = null;
  public exePath: string = null;
  public csvPath: string = null;
  public xmlValuePath: string = null;
  public csvBeforeValuePath: string = null;
  public csvInputValuePath: string = null;
  public updateKind: UpdateKinds = null;
  public row: number = null;
  public column: number = null;
  public splitter: string = null;
  public items: ExternalSubItem[] = [];
  public previousCsvBinary: string = null;
  public pasteDestinations: RelationalActionParameter[] = [];
  public parentAlertInformations: RelationalActionParameter[] = [];
  public enabledAutoAdjustFontSize: boolean = false;
  public autoNotePageSize: number = null;
  public autoNotePageKind: AutoNotePageKinds = null;
  public subName: string = null;    // 転記で使用する模様
  public enableNameGroupEmptyCheck: boolean = false;
  public isNaturalViewEnable: boolean;
  public naturalText: string;
 
  protected createCloneInstance(): ExternalDataItem {
    return new ExternalDataItem(this.originalValue);
  }

  protected applyMembers(clone: ExternalDataItem): void {
    super.applyMembers(clone);
    clone.paintStyleOnEditing = this.cloneValue(this.paintStyleOnEditing);
    clone.imeMode = this.cloneValue(this.imeMode);
    clone.enableSingleEmptyCheck = this.cloneValue(this.enableSingleEmptyCheck);
    clone.enableEmptyCheckConfirmation = this.cloneValue(this.enableEmptyCheckConfirmation);
    clone.alertGroupKey = this.cloneValue(this.alertGroupKey);
    clone.dataValidationKind = this.cloneValue(this.dataValidationKind);
    clone.exePath = this.cloneValue(this.exePath);
    clone.csvPath = this.cloneValue(this.csvPath);
    clone.xmlValuePath = this.cloneValue(this.xmlValuePath);
    clone.csvBeforeValuePath = this.cloneValue(this.csvBeforeValuePath);
    clone.csvInputValuePath = this.cloneValue(this.csvInputValuePath);
    clone.updateKind = this.cloneValue(this.updateKind);
    clone.row = this.cloneValue(this.row);
    clone.column = this.cloneValue(this.column);
    clone.splitter = this.cloneValue(this.splitter);
    clone.items = this.cloneValue(this.items);
    clone.previousCsvBinary = this.cloneValue(this.previousCsvBinary);
    clone.pasteDestinations = this.cloneValue(this.pasteDestinations);
    clone.parentAlertInformations = this.cloneValue(this.parentAlertInformations);
    clone.enabledAutoAdjustFontSize = this.cloneValue(this.enabledAutoAdjustFontSize);
    clone.autoNotePageSize = this.cloneValue(this.autoNotePageSize);
    clone.autoNotePageKind = this.cloneValue(this.autoNotePageKind);
    clone.subName = this.cloneValue(this.subName);
    clone.enableNameGroupEmptyCheck = this.cloneValue(this.enableNameGroupEmptyCheck);
    clone.isNaturalViewEnable = this.cloneValue(this.isNaturalViewEnable);
    clone.naturalText = this.cloneValue(this.naturalText);
  }

  get isEmpty(): boolean {
    return !this.value || this.value === "";
  }

  getGroupSelectKey(): string {
    return null;
  }

  isExternalDataCellParameterMatched(name: string, row: number, column: number): boolean {
    return (name === this.name)
      && (row === this.row)
      && (column === this.column);
  }

  getValueForExternalScript(): string {
    return this.value;
  }

  setValueFromExternalScript(value: string): void {
    this.value = value;
  }

  adjustFontSize(): void {
    if (!this.enabledAutoAdjustFontSize) { return; }
    const adjustedFontSize = this.getAdjustedFontSize(this.isMultiLine);
    this.textStyle.adjustedFontSize = adjustedFontSize;
  }

  get isMultiLine(): boolean {
    return this.textStyle.isMultiLine;
  }

  pasteTo(item: IPasteApplicable): void {
    const text = this.valueToText(this.value);
    item.applyPastedValue(text, undefined);
  }

  get isSuppressionActive(): boolean {
    return this.isEmpty;
  }

  public setText(text: string): void {
    this.value = text;
  }

  getDisplayName(): string {
    return this.name;
  }

  shouldValidateText(): boolean {
    return this.isFreeInputable && this.shouldInputFree;
  }

  isValueUpdated(item: any): boolean {
    return !item || (this.value !== item.value);
  }

  get currentNaturalText(): string {
    return this.isNaturalViewEnable && this.naturalText
      ? this.naturalText
      : this.text();
  }
}
